import { Rank, Image, Data, Annotation, Concept, Text } from 'api/grpc/resources';
import { Instance, types } from 'mobx-state-tree';

export const VisualUrlRank = types
  .model({
    url: types.string,
  })
  .views((self) => {
    return {
      toAPI(): Rank {
        const img = new Image();
        img.setUrl(self.url);

        const data = new Data();
        data.setImage(img);

        const annotation = new Annotation();
        annotation.setData(data);

        const rank = new Rank();
        rank.setAnnotation(annotation);

        return rank;
      },
    };
  });

export const InputIdSimilarityRank = types
  .model({
    inputId: types.string,
  })
  .views((self) => {
    return {
      toAPI(): Rank {
        const annotation = new Annotation();
        annotation.setInputId(self.inputId);

        const rank = new Rank();
        rank.setAnnotation(annotation);

        return rank;
      },
    };
  });

export const VisualBase64Rank = types
  .model({
    base64: types.string,
  })
  .views((self) => {
    return {
      toAPI(): Rank {
        const img = new Image();
        img.setBase64(self.base64);

        const data = new Data();
        data.setImage(img);

        const annotation = new Annotation();
        annotation.setData(data);

        const rank = new Rank();
        rank.setAnnotation(annotation);

        return rank;
      },
    };
  });

export const TextSimilarityRank = types
  .model({
    inputText: types.string,
  })
  .views((self) => {
    return {
      toAPI(): Rank {
        const text = new Text();
        text.setRaw(self.inputText);

        const data = new Data();
        data.setText(text);

        const annotation = new Annotation();
        annotation.setData(data);

        const rank = new Rank();
        rank.setAnnotation(annotation);

        return rank;
      },
    };
  });

function _createConceptRank(id: string, name: string): Rank {
  const concept = new Concept();
  concept.setName(name);
  concept.setId(id);
  concept.setValue(1);

  const data = new Data();
  data.setConceptsList([concept]);

  const annotation = new Annotation();
  annotation.setData(data);

  const rank = new Rank();
  rank.setAnnotation(annotation);

  return rank;
}

export const RankByPublicConcept = types
  .model({
    publicConcept: types.frozen<Concept>(),
  })
  .views((self) => {
    return {
      toAPI(): Rank {
        return _createConceptRank(self.publicConcept.getId(), self.publicConcept.getName());
      },
    };
  });

export const RankByAppConcept = types
  .model({
    appConcept: types.frozen<Concept>(),
  })
  .views((self) => {
    return {
      toAPI(): Rank {
        return _createConceptRank(self.appConcept.getId(), self.appConcept.getName());
      },
    };
  });

export const InputRank = types.union(
  VisualUrlRank,
  VisualBase64Rank,
  InputIdSimilarityRank,
  TextSimilarityRank,
  RankByPublicConcept,
  RankByAppConcept,
);

export function rankIsVisualUrl(rank: Instance<typeof InputRank>): rank is Instance<typeof VisualUrlRank> {
  const item = rank as Instance<typeof VisualUrlRank>;
  return item.url !== undefined;
}

export function rankIsVisualImageBase64(rank: Instance<typeof InputRank>): rank is Instance<typeof VisualBase64Rank> {
  const item = rank as Instance<typeof VisualBase64Rank>;
  return item.base64 !== undefined;
}

export function rankIsVisualInputId(rank: Instance<typeof InputRank>): rank is Instance<typeof InputIdSimilarityRank> {
  const item = rank as Instance<typeof InputIdSimilarityRank>;
  return item.inputId !== undefined;
}

export function rankIsByPublicConcept(rank: Instance<typeof InputRank>): rank is Instance<typeof RankByPublicConcept> {
  const item = rank as Instance<typeof RankByPublicConcept>;
  return item.publicConcept !== undefined;
}

export function rankIsByAppConcept(rank: Instance<typeof InputRank>): rank is Instance<typeof RankByAppConcept> {
  const item = rank as Instance<typeof RankByAppConcept>;
  return item.appConcept !== undefined;
}

export function rankIsTextSimilarityRank(rank: Instance<typeof InputRank>): rank is Instance<typeof TextSimilarityRank> {
  const item = rank as Instance<typeof TextSimilarityRank>;
  return item.inputText !== undefined;
}
